import { useEffect, useState, useCallback } from 'react';
import { View, ScrollView, Platform, useWindowDimensions } from 'react-native';
import { Appbar, Card, Text, TextInput, Chip, ActivityIndicator, List, useTheme, Surface, Button, Checkbox, Dialog, Portal } from 'react-native-paper';
import { Sidebar, SIDEBAR_WIDTH } from '../../../src/components/Sidebar';
import Logo from '../../../src/components/Logo';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'expo-router';
import { useUser } from '../../../src/hooks/useUser';
import { useProfile } from '../../../src/hooks/useProfile';
import { useFocusEffect } from '@react-navigation/native';
import { getCompanyVehicles, transferVehicle } from '../../../src/api/apiService';
import { AddVehicleModal } from '../../../src/components/AddVehicleModal';
import { TransferModal } from '../../../src/components/TransferModal';
import { API_ENDPOINTS } from '../../../src/api/config';
import apiService from '../../../src/api/apiService';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

type VehicleRow = {
  id: number;
  registration_number: string;
  make?: string;
  model?: string;
  year?: number;
  fuel_type?: string;
};

type PaginatedResponse = {
  results: VehicleRow[];
  count: number;
  page: number;
  page_size: number;
  total_pages: number;
  next: boolean;
  previous: boolean;
};

export default function VehiclesListScreen() {
  const { t } = useTranslation('manageVehicles');
  const router = useRouter();
  const theme = useTheme();
  const { width, height } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const isLargeScreen = width > 1024;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { handleLogout, userData } = useUser();
  const { hasOwner, hasDriver, selectedProfile, updateProfile } = useProfile(userData?.groups || []);
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState<VehicleRow[]>([]);
  const [query, setQuery] = useState('');
  const [orderDesc, setOrderDesc] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [selectedVehicleIds, setSelectedVehicleIds] = useState<Set<number>>(new Set());
  const [transferModalVisible, setTransferModalVisible] = useState(false);
  const [bulkTransferring, setBulkTransferring] = useState(false);
  const [resultDialogVisible, setResultDialogVisible] = useState(false);
  const [resultMessage, setResultMessage] = useState('');
  const [relatedDriversCount, setRelatedDriversCount] = useState(0);

  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await getCompanyVehicles(
        page,
        pageSize,
        query.trim() || undefined,
        orderDesc ? '-registration_number' : 'registration_number'
      );
      
      if (resp.ok) {
        const responseData = resp.data as PaginatedResponse;
        setVehicles(responseData.results || []);
        setTotalPages(responseData.total_pages || 1);
        setTotalCount(responseData.count || 0);
      } else {
        setVehicles([]);
        setTotalPages(1);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
      setVehicles([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, query, orderDesc]);

  useEffect(() => {
    fetchVehicles();
  }, [fetchVehicles]);

  // Reset to page 1 when query or ordering changes
  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
  }, [query, orderDesc]);

  // Refresh vehicles list when screen comes into focus
  useFocusEffect(
    useCallback(() => {
      fetchVehicles();
    }, [fetchVehicles])
  );

  // Selection handlers
  const toggleVehicleSelection = (vehicleId: number) => {
    setSelectedVehicleIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(vehicleId)) {
        newSet.delete(vehicleId);
      } else {
        newSet.add(vehicleId);
      }
      return newSet;
    });
  };

  // Fetch all vehicles matching current filters (for select all)
  const fetchAllMatchingVehicles = useCallback(async (): Promise<number[]> => {
    try {
      // Fetch all pages if needed
      const allIds: number[] = [];
      let currentPage = 1;
      let hasMore = true;
      const pageSize = 100; // Use reasonable page size
      
      while (hasMore) {
        const resp = await getCompanyVehicles(
          currentPage,
          pageSize,
          query.trim() || undefined,
          orderDesc ? '-registration_number' : 'registration_number'
        );
        
        if (resp.ok) {
          const responseData = resp.data as PaginatedResponse;
          const pageIds = (responseData.results || []).map(v => v.id);
          allIds.push(...pageIds);
          
          // Check if there are more pages
          hasMore = responseData.next === true && pageIds.length === pageSize;
          currentPage++;
          
          // Safety limit to prevent infinite loops
          if (currentPage > 100) break;
        } else {
          hasMore = false;
        }
      }
      
      return allIds;
    } catch (error) {
      console.error('Error fetching all vehicles:', error);
      // Fallback to current page if fetch fails
      return vehicles.map(v => v.id);
    }
  }, [query, orderDesc, vehicles]);

  const toggleSelectAll = async () => {
    // Check if all matching items are selected
    const allCurrentPageSelected = vehicles.length > 0 && vehicles.every(v => selectedVehicleIds.has(v.id));
    const allMatchingSelected = allCurrentPageSelected && selectedVehicleIds.size >= totalCount;
    
    if (allMatchingSelected) {
      // Deselect all
      setSelectedVehicleIds(new Set());
    } else {
      // Select all matching vehicles (across all pages)
      const allMatchingIds = await fetchAllMatchingVehicles();
      setSelectedVehicleIds(new Set(allMatchingIds));
    }
  };

  const clearSelection = () => {
    setSelectedVehicleIds(new Set());
  };

  // Fetch related drivers count for selected vehicles
  const fetchRelatedDriversCount = useCallback(async () => {
    if (selectedVehicleIds.size === 0) {
      setRelatedDriversCount(0);
      return;
    }

    try {
      // Fetch vehicle details to get driver counts
      let totalDrivers = 0;
      for (const vehicleId of selectedVehicleIds) {
        try {
          const response = await apiService.get(`${API_ENDPOINTS.VEHICLES}${vehicleId}/`);
          if (response.ok) {
            const vehicle = response.data as any;
            // Assuming the API returns drivers array or drivers_count
            if (vehicle.drivers && Array.isArray(vehicle.drivers)) {
              totalDrivers += vehicle.drivers.length;
            } else if (vehicle.drivers_count) {
              totalDrivers += vehicle.drivers_count;
            }
          }
        } catch (e) {
          // Ignore errors for individual vehicles
        }
      }
      setRelatedDriversCount(totalDrivers);
    } catch (e) {
      setRelatedDriversCount(0);
    }
  }, [selectedVehicleIds]);

  useEffect(() => {
    if (transferModalVisible) {
      fetchRelatedDriversCount();
    }
  }, [transferModalVisible, fetchRelatedDriversCount]);

  // Bulk transfer handler
  const handleBulkTransfer = async (targetCompanyId: number, transferRelatedDrivers: boolean) => {
    setBulkTransferring(true);
    const vehicleIds = Array.from(selectedVehicleIds);
    let successCount = 0;
    let errorCount = 0;
    const errors: string[] = [];

    try {
      for (const vehicleId of vehicleIds) {
        try {
          const response = await transferVehicle({
            vehicle_id: vehicleId,
            target_company_id: targetCompanyId,
            transfer_related_drivers: transferRelatedDrivers
          });
          
          if (response.ok) {
            successCount++;
          } else {
            errorCount++;
            const errorData = response.data as any;
            const isLicenseError = errorData?.license_error || errorData?.error === 'license_required' || errorData?.detail?.includes('license');
            const errorMsg = isLicenseError
              ? t('bulkActions.transferLicenseErrorDrivers', 'Target company has no available driver licenses to transfer related drivers.')
              : (errorData?.error || errorData?.detail || 'Unknown error');
            errors.push(`${vehicleId}: ${errorMsg}`);
          }
        } catch (e: any) {
          errorCount++;
          errors.push(`${vehicleId}: ${e.message || 'Network error'}`);
        }
      }

      setResultMessage(
        t('bulkActions.transferResult', 'Transferred {{success}} vehicle(s). {{errors}} error(s).', {
          success: successCount,
          errors: errorCount
        }) + (errors.length > 0 ? `\n\nErrors:\n${errors.slice(0, 5).join('\n')}${errors.length > 5 ? `\n... and ${errors.length - 5} more` : ''}` : '')
      );
      setResultDialogVisible(true);
      clearSelection();
      fetchVehicles();
    } catch (e: any) {
      setResultMessage(t('bulkActions.transferError', 'Error transferring vehicles: {{error}}', { error: e.message }));
      setResultDialogVisible(true);
    } finally {
      setBulkTransferring(false);
      setTransferModalVisible(false);
    }
  };

  // Calcular height total do header incluindo status bar
  const headerHeight = 48; // Altura compacta para mobile
  const topPadding = Platform.OS === 'ios' ? insets.top : Platform.OS === 'android' ? insets.top : 0;
  const totalHeaderHeight = isLargeScreen ? 0 : headerHeight + topPadding;

  const renderHeader = () => {
    if (isLargeScreen) return null;
    return (
      <Appbar.Header style={{ 
        height: headerHeight + topPadding, 
        backgroundColor: theme.colors.surface, 
        position: 'absolute', 
        top: 0, 
        left: 0, 
        right: 0, 
        zIndex: 1,
        alignItems: 'center',
        paddingTop: topPadding,
      }}>
        <Appbar.Action icon="menu" onPress={() => setSidebarOpen(true)} color={theme.colors.onSurface} />
        <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}>
          <Logo size={Platform.OS === 'web' ? 'medium' : 'mobile'} />
        </View>
        <View style={{ width: 48 }} />
      </Appbar.Header>
    );
  };

  return (
    <View style={{ flex: 1 }}>
      {isLargeScreen && (
        <Sidebar isOpen={true} onClose={() => {}} screenWidth={width} onLogout={handleLogout} isOwner={hasOwner} isDriver={hasDriver} selectedProfile={selectedProfile} onSwitchProfile={hasDriver && hasOwner ? updateProfile : undefined} />
      )}
      <Surface style={{ flex: 1, backgroundColor: theme.colors.background, ...(isLargeScreen ? { marginLeft: SIDEBAR_WIDTH } : {}) }}>
        {renderHeader()}
        <Appbar.Header style={isLargeScreen ? {} : { display: 'none' }}>
          <Appbar.Content title={t('vehicles', 'Vehicles')} />
          <Appbar.Action icon="plus" onPress={() => setAddModalVisible(true)} />
        </Appbar.Header>
        <ScrollView style={{ flex: 1, padding: 12, ...(isLargeScreen ? {} : { marginTop: totalHeaderHeight }) }}>
          <Card style={{ marginBottom: 12 }}>
            <Card.Content>
              {/* Search and Order */}
              <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                <View style={{ flex: 1 }}>
                  <TextInput 
                    mode="outlined" 
                    placeholder={t('search', 'Search by registration, make, or model')} 
                    value={query} 
                    onChangeText={(v) => {
                      setQuery(v);
                      setPage(1); // Reset to page 1 when search changes
                    }}
                    left={<TextInput.Icon icon="magnify" />}
                    right={query ? <TextInput.Icon icon="close" onPress={() => {
                      setQuery('');
                      setPage(1);
                    }} /> : undefined}
                  />
                </View>
                <Chip 
                  selected={true} 
                  onPress={() => setOrderDesc(!orderDesc)}
                  icon={orderDesc ? 'sort-alphabetical-descending' : 'sort-alphabetical-ascending'}
                >
                  {orderDesc ? t('descending', 'Z-A') : t('ascending', 'A-Z')}
                </Chip>
              </View>
              {/* Add Vehicle Button */}
              <Button
                mode="contained"
                icon="plus"
                onPress={() => setAddModalVisible(true)}
                style={{ marginTop: 8 }}
              >
                {t('addVehicle', 'Add Vehicle')}
              </Button>
            </Card.Content>
          </Card>

          {/* Bulk Actions Bar */}
          {selectedVehicleIds.size > 0 && (
            <Card style={{ marginBottom: 12, backgroundColor: theme.colors.primaryContainer }}>
              <Card.Content>
                <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <Checkbox
                      status={selectedVehicleIds.size === vehicles.length ? 'checked' : 'indeterminate'}
                      onPress={toggleSelectAll}
                    />
                    <Text variant="bodyLarge">
                      {t('bulkActions.selected', 'Selected: {{count}}', { count: selectedVehicleIds.size })}
                    </Text>
                  </View>
                  <View style={{ flexDirection: 'row', gap: 8 }}>
                    <Button
                      mode="contained"
                      onPress={() => setTransferModalVisible(true)}
                      loading={bulkTransferring}
                      disabled={bulkTransferring}
                      icon="swap-horizontal"
                    >
                      {t('bulkActions.transfer', 'Transfer')}
                    </Button>
                    <Button
                      mode="text"
                      onPress={clearSelection}
                      icon="close"
                    >
                      {t('common.cancel', 'Cancel')}
                    </Button>
                  </View>
                </View>
              </Card.Content>
            </Card>
          )}

          <Card>
            <Card.Content>
              {loading ? (
                <ActivityIndicator />
              ) : vehicles.length === 0 ? (
                <Text style={{ color: theme.colors.onSurfaceVariant }}>{t('noResults','No results')}</Text>
              ) : (
                <List.Section>
                  {/* Select All Header */}
                  <View style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 8, paddingHorizontal: 16 }}>
                    <Checkbox
                      status={
                        selectedVehicleIds.size > 0 && 
                        vehicles.length > 0 && 
                        vehicles.every(v => selectedVehicleIds.has(v.id)) &&
                        (selectedVehicleIds.size === totalCount || selectedVehicleIds.size > totalCount)
                          ? 'checked' 
                          : selectedVehicleIds.size > 0 
                          ? 'indeterminate' 
                          : 'unchecked'
                      }
                      onPress={toggleSelectAll}
                    />
                    <Text variant="bodyMedium" style={{ marginLeft: 8 }}>
                      {t('bulkActions.selectAll', 'Select All')} 
                      {selectedVehicleIds.size > 0 && ` (${selectedVehicleIds.size}${totalCount > 0 && totalCount !== selectedVehicleIds.size ? ` / ${totalCount}` : ''})`}
                    </Text>
                  </View>
                  {vehicles.map(v => (
                    <List.Item
                      key={v.id}
                      title={v.registration_number}
                      description={[v.make, v.model, v.year].filter(Boolean).join(' ') || t('noDetails', 'No details')}
                      left={props => (
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                          <Checkbox
                            status={selectedVehicleIds.has(v.id) ? 'checked' : 'unchecked'}
                            onPress={() => toggleVehicleSelection(v.id)}
                          />
                          <List.Icon {...props} icon="car" />
                        </View>
                      )}
                      right={props => (
                        <View style={{ alignItems: 'flex-end', gap: 4 }}>
                          {v.fuel_type && (
                            <Chip 
                              mode="flat" 
                              compact
                              textStyle={{ fontSize: 11 }}
                            >
                              {t(`fuelType.${v.fuel_type}`, v.fuel_type)}
                            </Chip>
                          )}
                        </View>
                      )}
                      onPress={() => router.push(`/dashboard/vehicles/${v.id}`)}
                    />
                  ))}
                </List.Section>
              )}
            </Card.Content>
          </Card>

          {totalPages > 1 && (
            <Card style={{ marginTop: 12 }}>
              <Card.Content>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <Button 
                    mode="outlined" 
                    onPress={() => setPage(Math.max(1, page - 1))} 
                    disabled={page <= 1}
                    icon="chevron-left"
                  >
                    {t('previous', 'Previous')}
                  </Button>
                  <Text style={{ minWidth: 60, textAlign: 'center' }}>
                    {t('page', 'Page')} {page} {t('of', 'of')} {totalPages}
                  </Text>
                  <Button 
                    mode="outlined" 
                    onPress={() => setPage(Math.min(totalPages, page + 1))} 
                    disabled={page >= totalPages}
                    icon="chevron-right"
                    contentStyle={{ flexDirection: 'row-reverse' }}
                  >
                    {t('next', 'Next')}
                  </Button>
                </View>
                {totalCount > 0 && (
                  <Text style={{ textAlign: 'center', marginTop: 8, fontSize: 12, color: theme.colors.onSurfaceVariant }}>
                    {t('showing', 'Showing')} {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalCount)} {t('of', 'of')} {totalCount} {t('vehicles', 'vehicles')}
                  </Text>
                )}
              </Card.Content>
            </Card>
          )}
        </ScrollView>
        {!isLargeScreen && (
          <View style={{ position: 'absolute', bottom: 16, right: 16 }}>
            <Button
              mode="contained"
              icon="plus"
              onPress={() => setAddModalVisible(true)}
              style={{ borderRadius: 28 }}
              contentStyle={{ paddingHorizontal: 16 }}
            >
              {t('add', 'Add')}
            </Button>
          </View>
        )}
        {!isLargeScreen && (
          <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} screenWidth={width} onLogout={handleLogout} isOwner={hasOwner} isDriver={hasDriver} selectedProfile={selectedProfile} onSwitchProfile={hasDriver && hasOwner ? updateProfile : undefined} />
        )}
      </Surface>
      <AddVehicleModal
        visible={addModalVisible}
        onDismiss={() => setAddModalVisible(false)}
        onSuccess={() => {
          fetchVehicles();
          setAddModalVisible(false);
        }}
      />

      {/* Transfer Modal */}
      <TransferModal
        visible={transferModalVisible}
        onDismiss={() => setTransferModalVisible(false)}
        onTransfer={handleBulkTransfer}
        title={t('bulkActions.transferVehicles', 'Transfer {{count}} Vehicles', { count: selectedVehicleIds.size })}
        description={t('bulkActions.transferVehiclesDescription', 'Select the target company to transfer the selected vehicles to.')}
        transferRelatedDrivers={true}
        relatedItemsCount={relatedDriversCount}
        itemCount={selectedVehicleIds.size}
      />

      {/* Result Dialog */}
      <Portal>
        <Dialog 
          visible={resultDialogVisible} 
          onDismiss={() => setResultDialogVisible(false)}
          style={isLargeScreen ? { maxWidth: 600, alignSelf: 'center', width: '90%' } : undefined}
        >
          <Dialog.Title>{t('bulkActions.result', 'Operation Result')}</Dialog.Title>
          <Dialog.Content>
            <Text>{resultMessage}</Text>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setResultDialogVisible(false)}>{t('common.ok', 'OK')}</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </View>
  );
}
